import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Id } from "../../convex/_generated/dataModel";
import { useIsAdmin } from "../hooks/useIsAdmin";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../reactvideoeditor/pro/components/ui/dropdown-menu";
import { clearAutosave } from "../reactvideoeditor/pro/utils/general/indexdb-helper";
import { toast } from "sonner";

export function Dashboard() {
  const navigate = useNavigate();
  const user = useQuery(api.users.getCurrentUser);
  const createUserProfile = useMutation(api.users.createUserProfile);
  const projects = useQuery(api.projects.listProjects);
  const createProject = useMutation(api.projects.createProject);
  const deleteProject = useMutation(api.projects.deleteProject);
  const duplicateProject = useMutation(api.projects.duplicateProject);
  const [deletingProjectId, setDeletingProjectId] = useState<Id<"projects"> | null>(null);
  const [duplicatingProjectId, setDuplicatingProjectId] = useState<Id<"projects"> | null>(null);
  const { isAdmin } = useIsAdmin();

  // Create profile if it doesn't exist
  useEffect(() => {
    if (user && !user.profile) {
      void createUserProfile();
    }
  }, [user, createUserProfile]);

  const handleCreateProject = async () => {
    try {
      const projectId = await createProject({
        type: "default",
      });
      navigate(`/editor?projectId=${projectId}`);
    } catch (error) {
      console.error('Failed to create project:', error);
    }
  };

  const handleProjectClick = (projectId: Id<"projects">) => {
    navigate(`/editor?projectId=${projectId}`);
  };

  const handleDuplicate = async (projectId: Id<"projects">, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    setDuplicatingProjectId(projectId);
    try {
      const newProjectId = await duplicateProject({ projectId });
      toast.success("Project duplicated successfully");
      // Optionally navigate to the new project
      // navigate(`/editor?projectId=${newProjectId}`);
    } catch (error) {
      console.error("Failed to duplicate project:", error);
      toast.error("Failed to duplicate project");
    } finally {
      setDuplicatingProjectId(null);
    }
  };

  const handleDelete = async (projectId: Id<"projects">, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    if (!confirm("Are you sure you want to delete this project? This action cannot be undone.")) {
      return;
    }

    setDeletingProjectId(projectId);
    try {
      // Clear IndexedDB autosave for this project
      try {
        await clearAutosave(projectId);
      } catch (error) {
        console.warn("Failed to clear autosave from IndexedDB:", error);
      }

      await deleteProject({ projectId });
      toast.success("Project deleted successfully");
    } catch (error) {
      console.error("Failed to delete project:", error);
      toast.error("Failed to delete project");
    } finally {
      setDeletingProjectId(null);
    }
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (user === undefined) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6">
      <div className="flex justify-end items-center gap-3 mb-6">
        {isAdmin && (
          <button
            onClick={() => navigate('/admin/audio')}
            className="px-4 py-2 bg-secondary text-white font-semibold rounded-lg hover:bg-secondary/80 transition-all shadow-sm hover:shadow-md text-sm"
          >
            Admin: Audio Library
          </button>
        )}
        <button
          onClick={handleCreateProject}
          className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-sm hover:shadow-md"
        >
          + Create New Project
        </button>
      </div>

      {projects === undefined ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : projects.length === 0 ? (
        <div className="bg-background rounded-lg shadow-sm border border-border p-12">
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mb-6">
              <svg
                className="w-8 h-8 sm:w-10 sm:h-10 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
              No projects yet
            </h2>
            <p className="text-muted-foreground mb-6 max-w-md">
              Create your first video project to get started with our powerful AI-powered video editor.
            </p>
            <button
              onClick={handleCreateProject}
              className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all shadow-sm hover:shadow-md text-base"
            >
              Create Your First Project
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {projects.map((project) => (
            <div
              key={project._id}
              onClick={() => handleProjectClick(project._id)}
              className="bg-card rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer overflow-hidden border border-border relative group"
            >
              {/* Three Dots Menu */}
              <div className="absolute top-2 right-2 z-10">
                <DropdownMenu>
                  <DropdownMenuTrigger
                    onClick={(e) => e.stopPropagation()}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-md hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <svg
                      className="w-5 h-5 text-muted-foreground"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                      />
                    </svg>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40">
                    <DropdownMenuItem
                      onClick={(e) => handleDuplicate(project._id, e)}
                      disabled={duplicatingProjectId === project._id}
                      className="cursor-pointer"
                    >
                      {duplicatingProjectId === project._id ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                          Duplicating...
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-4 h-4 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                          </svg>
                          Duplicate
                        </>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => handleDelete(project._id, e)}
                      disabled={deletingProjectId === project._id}
                      className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                    >
                      {deletingProjectId === project._id ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></div>
                          Deleting...
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-4 h-4 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                          Delete
                        </>
                      )}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {project.thumbnailUrl ? (
                <div className="w-full h-48 bg-gray-100 overflow-hidden">
                  <img
                    src={project.thumbnailUrl}
                    alt={project.videoFileName || 'Project thumbnail'}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              ) : project.thumbnailFileId ? (
                <div className="w-full h-48 bg-muted flex items-center justify-center">
                  <div className="animate-pulse text-muted-foreground">Loading...</div>
                </div>
              ) : (
                <div className="w-full h-48 bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                  <span className="text-white text-2xl font-bold">
                    {project.videoFileName?.charAt(0).toUpperCase() || 'P'}
                  </span>
                </div>
              )}
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold px-2 py-1 rounded bg-primary/10 text-primary">
                    {project.type}
                  </span>
                  {project.videoFileName && (
                    <span className="text-xs text-muted-foreground truncate max-w-[120px]">
                      {project.videoFileName}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Updated {formatDate(project.updatedAt)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

